import axios from 'axios';
import { supabase } from '../config/supabase';
import config from '../config/setup';

// Always use port 5001 in development, or use environment variable in production
/* const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'http://localhost:5001')
  : 'http://localhost:5001'; */

const API_BASE_URL = config.api.baseUrl;
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token and log requests
api.interceptors.request.use(
  async (requestConfig) => {
    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) throw error;
      
      if (session?.access_token) {
        requestConfig.headers.Authorization = `Bearer ${session.access_token}`;
      }

      // If the request data is FormData, let the browser set the Content-Type header
      if (requestConfig.data instanceof FormData) {
        delete requestConfig.headers['Content-Type'];
        // Ensure the browser sets the correct boundary
        requestConfig.headers['Accept'] = 'application/json';
      }

      // Log the full request URL for debugging if console logging is enabled
      if (config.logging.enableConsole) {
        const fullUrl = requestConfig.baseURL 
          ? requestConfig.baseURL + requestConfig.url 
          : requestConfig.url;
        console.log('🔍 API Request Debug:');
        console.log('  - URL:', fullUrl);
        console.log('  - Method:', requestConfig.method);
        console.log('  - Headers:', {
          'Content-Type': requestConfig.headers['Content-Type'],
          'Authorization': requestConfig.headers.Authorization ? 'Bearer [TOKEN]' : 'None'
        });
        console.log('  - Data:', requestConfig.data instanceof FormData ? 'FormData' : requestConfig.data);
      }
      
      return requestConfig;
    } catch (error) {
      console.error('Auth interceptor error:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    if (config.logging.enableConsole) {
      console.log('🔍 API Response Debug:');
      console.log('  - Status:', response.status);
      console.log('  - URL:', response.config.url);
      console.log('  - Data:', response.data);
    }
    return response;
  },
  async (error) => {
    if (config.logging.enableConsole) {
      console.log('🔍 API Error Debug:');
      console.log('  - Status:', error.response?.status);
      console.log('  - URL:', error.config?.url);
      console.log('  - Error Data:', error.response?.data);
      console.log('  - Error Message:', error.message);
    }
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError) throw refreshError;
        
        if (session?.access_token) {
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, redirect to login or handle as needed
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Work Plans API
export const workPlanApi = {
  getAll: () => api.get('/api/work-plans').then(res => res.data),
  getActive: () => api.get('/api/work-plans/active').then(res => res.data),
  get: (id) => api.get(`/api/work-plans/${id}`).then(res => res.data),
  create: (data) => api.post('/api/work-plans', data).then(res => res.data),
  update: (id, data) => api.put(`/api/work-plans/${id}`, data).then(res => res.data),
  close: (id, data) => api.post(`/api/work-plans/${id}/close`, data).then(res => res.data),
  delete: (id) => api.delete(`/api/work-plans/${id}`).then(res => res.data),
  getUnclosed: () => api.get('/api/work-plans/unclosed').then(res => res.data),
  addTasks: (id, tasks) => api.post(`/api/work-plans/${id}/tasks`, { tasks }).then(res => res.data),
  getTodayClosed: () => api.get('/api/work-plans/today-closed').then(res => res.data),
  uploadTaskAttachment: (workPlanId, taskId, file) => {
    const formData = new FormData();
    formData.append('files', file);
    return api.post(`/api/work-plans/${workPlanId}/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(res => {
      // Return the entire task with updated work plan attachments
      return res.data;
    });
  },
  deleteWorkPlanAttachment: (workPlanId, taskId, attachmentId) => {
    return api.delete(`/api/work-plans/${workPlanId}/tasks/${taskId}/attachments/${attachmentId}`).then(res => res.data);
  },
  evaluate: (id, data) => api.post(`/api/work-plans/${id}/evaluate`, data || {}).then(res => res.data),
  getNextQuestion: (id, data) => api.post(`/api/work-plans/${id}/evaluate/next-question`, data).then(res => res.data),
  saveEvaluation: (id, evaluationData) => api.post(`/api/work-plans/${id}/evaluations`, evaluationData).then(res => res.data)
};

// NexGate API
export const nexgateApi = {
  getAll: (params) => api.get('/api/nexgate', { params }).then(res => res.data),
  getById: (id) => api.get(`/api/nexgate/${id}`).then(res => res.data),
  create: (data) => api.post('/api/nexgate', data).then(res => res.data),
  update: (id, data) => api.put(`/api/nexgate/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/api/nexgate/${id}`).then(res => res.data),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/nexgate/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  deleteAttachment: (attachmentId) => api.delete(`/api/nexgate/attachments/${attachmentId}`).then(res => res.data),
  addComment: (id, comment) => api.post(`/api/nexgate/${id}/comments`, { comment }).then(res => res.data),
  getResources: (params) => api.get('/api/nexgate/resources/available', { params }).then(res => res.data),
  createMatch: (id, data) => api.post(`/api/nexgate/${id}/matches`, data).then(res => res.data),
  updateMatch: (matchId, data) => api.put(`/api/nexgate/matches/${matchId}`, data).then(res => res.data)
};

// Tasks API
export const taskApi = {
  getAll: () => api.get('/api/tasks').then(res => res.data),
  get: (id) => api.get(`/api/tasks/${id}`).then(res => res.data),
  create: (data) => {
    // If data is FormData, let the browser set the Content-Type header
    if (data instanceof FormData) {
      return api.post('/api/tasks', data, {
        headers: {
          // Remove Content-Type header to let the browser set it with the boundary
          'Content-Type': undefined
        },
        // Add proper configuration for file uploads
        transformRequest: [(data) => {
          return data;
        }],
        onUploadProgress: (progressEvent) => {
          // Upload progress tracking (silent)
        }
      }).then(res => {
        return res.data;
      }).catch(error => {
        console.error('Task creation error:', error.response?.data || error.message);
        throw error;
      });
    }
    // Otherwise, send as JSON
    return api.post('/api/tasks', data).then(res => res.data);
  },
  update: (id, data) => api.put(`/api/tasks/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/api/tasks/${id}`).then(res => res.data),
  uploadAttachment: (taskId, file) => {
    const formData = new FormData();
    formData.append('files', file);
    return api.post(`/api/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(res => {
      // Return the new attachment (last one in the list)
      const attachments = res.data.attachments || [];
      return attachments[attachments.length - 1];
    });
  },
  deleteAttachment: (taskId, attachmentId) => {
    return api.delete(`/api/tasks/${taskId}/attachments/${attachmentId}`).then(res => res.data);
  },
  getUserGoals: (date) => {
    const params = date ? { date } : {};
    return api.get('/api/tasks/user-goals', { params }).then(res => res.data);
  },
  getByProject: (projectId) => api.get(`/api/tasks/project/${projectId}`).then(res => res.data),
  // Task linking methods
  getLinks: (taskId) => api.get(`/api/tasks/${taskId}/links`).then(res => res.data),
  createLink: (taskId, linkedTaskId, relationshipType) => {
    return api.post(`/api/tasks/${taskId}/links`, {
      linked_task_id: linkedTaskId,
      relationship_type: relationshipType
    }).then(res => res.data);
  },
  updateLink: (taskId, linkId, relationshipType) => {
    return api.put(`/api/tasks/${taskId}/links/${linkId}`, {
      relationship_type: relationshipType
    }).then(res => res.data);
  },
  deleteLink: (taskId, linkId) => {
    return api.delete(`/api/tasks/${taskId}/links/${linkId}`).then(res => res.data);
  }
};

// Tasks API V2 - Paginated project tasks for ProjectDetailsV2
export const taskApiV2 = {
  getProjectTasks: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page);
    if (params.limit) searchParams.set('limit', params.limit);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.status?.length) params.status.forEach(s => searchParams.append('status', s));
    if (params.createdBy) searchParams.set('createdBy', params.createdBy);
    if (params.assignedTo) searchParams.set('assignedTo', params.assignedTo);
    if (params.taskType) searchParams.set('taskType', params.taskType);
    if (params.searchQuery) searchParams.set('searchQuery', params.searchQuery);
    if (params.withLinksOnly) searchParams.set('withLinksOnly', 'true');
    if (params.selectedTagId) searchParams.set('selectedTagId', params.selectedTagId);
    if (params.date) searchParams.set('date', params.date);
    const qs = searchParams.toString();
    return api.get(`/api/tasks/v2/project/${projectId}${qs ? `?${qs}` : ''}`).then(res => res.data);
  },
  getGroups: (projectId, groupingMode, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.date) searchParams.set('date', params.date);
    if (params.status?.length) params.status.forEach(s => searchParams.append('status', s));
    if (params.createdBy) searchParams.set('createdBy', params.createdBy);
    if (params.assignedTo) searchParams.set('assignedTo', params.assignedTo);
    if (params.taskType) searchParams.set('taskType', params.taskType);
    if (params.searchQuery) searchParams.set('searchQuery', params.searchQuery);
    if (params.withLinksOnly) searchParams.set('withLinksOnly', 'true');
    if (params.selectedTagId) searchParams.set('selectedTagId', params.selectedTagId);
    const qs = searchParams.toString();
    return api.get(`/api/tasks/v2/project/${projectId}/groups/${groupingMode}${qs ? `?${qs}` : ''}`).then(res => res.data);
  },
  getGroupTasks: (projectId, groupingMode, groupId, params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', params.page ?? 1);
    if (params.limit) searchParams.set('limit', params.limit);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.status?.length) params.status.forEach(s => searchParams.append('status', s));
    if (params.createdBy) searchParams.set('createdBy', params.createdBy);
    if (params.assignedTo) searchParams.set('assignedTo', params.assignedTo);
    if (params.taskType) searchParams.set('taskType', params.taskType);
    if (params.searchQuery) searchParams.set('searchQuery', params.searchQuery);
    if (params.withLinksOnly) searchParams.set('withLinksOnly', 'true');
    if (params.selectedTagId) searchParams.set('selectedTagId', params.selectedTagId);
    if (params.date) searchParams.set('date', params.date);
    const qs = searchParams.toString();
    return api.get(`/api/tasks/v2/project/${projectId}/groups/${groupingMode}/${encodeURIComponent(groupId)}/tasks${qs ? `?${qs}` : ''}`).then(res => res.data);
  }
};

// Feature Tags API
export const featureTagApi = {
  getByProject: (projectId) => api.get(`/api/feature-tags/project/${projectId}`).then(res => res.data),
  getAll: () => api.get('/api/feature-tags').then(res => res.data),
  get: (id) => api.get(`/api/feature-tags/${id}`).then(res => res.data),
  create: (data) => api.post('/api/feature-tags', data).then(res => res.data),
  update: (id, data) => api.put(`/api/feature-tags/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/api/feature-tags/${id}`).then(res => res.data),
  // Due date specific methods
  getOverdue: (projectId) => api.get(`/api/feature-tags/project/${projectId}/overdue`).then(res => res.data),
  getUpcoming: (projectId, days = 7) => api.get(`/api/feature-tags/project/${projectId}/upcoming`, { 
    params: { days } 
  }).then(res => res.data),
  updateDueDate: (id, dueDate) => api.patch(`/api/feature-tags/${id}/due-date`, { due_date: dueDate }).then(res => res.data),
  // Description methods
  updateDescription: (id, description) => api.put(`/api/feature-tags/${id}`, { description }).then(res => res.data),
  // Artifacts methods
  getArtifacts: (id) => api.get(`/api/feature-tags/${id}/artifacts`).then(res => res.data),
  uploadArtifacts: (id, files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return api.post(`/api/feature-tags/${id}/artifacts`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then(res => res.data);
  },
  deleteArtifact: (tagId, artifactId) => api.delete(`/api/feature-tags/${tagId}/artifacts/${artifactId}`).then(res => res.data),
  // Links methods
  getLinks: (id) => api.get(`/api/feature-tags/${id}/links`).then(res => res.data),
  createLink: (id, linkData) => api.post(`/api/feature-tags/${id}/links`, linkData).then(res => res.data),
  updateLink: (tagId, linkId, linkData) => api.put(`/api/feature-tags/${tagId}/links/${linkId}`, linkData).then(res => res.data),
  deleteLink: (tagId, linkId) => api.delete(`/api/feature-tags/${tagId}/links/${linkId}`).then(res => res.data)
};

// Users API
export const userApi = {
  getRole: () => api.get('/api/users/role').then(res => res.data)
};

// Finance API
export const financeApi = {
  getResources: () => api.get('/api/finance/resources').then(res => res.data),
  getMonthlyAttendance: (year, month) =>
    api.get('/api/finance/attendance/monthly', { params: { year, month } }).then(res => res.data),
  upsertPayrollProfile: (resourceId, payload) =>
    api.post(`/api/finance/resources/${resourceId}/payroll-profile`, payload).then(res => res.data),
  createSalaryRevision: (resourceId, payload) =>
    api.post(`/api/finance/resources/${resourceId}/salary-revisions`, payload).then(res => res.data),
  generatePayslip: async ({ resourceId, year, month }) => {
    const response = await api.post(
      '/api/finance/payslips/generate',
      { resourceId, year, month },
      { responseType: 'blob' }
    );

    const contentDisposition = response.headers?.['content-disposition'] || '';
    const match = /filename="([^"]+)"/.exec(contentDisposition);
    const filename = match?.[1];

    return { blob: response.data, filename };
  },
  generateCustomPayslip: async ({ resourceId, year, month, lopDays }) => {
    const response = await api.post(
      '/api/finance/payslips/generate-custom',
      { resourceId, year, month, lopDays },
      { responseType: 'blob' }
    );

    const contentDisposition = response.headers?.['content-disposition'] || '';
    const match = /filename="([^"]+)"/.exec(contentDisposition);
    const filename = match?.[1];

    return { blob: response.data, filename };
  },
  getCompanyBankSettings: () => api.get('/api/finance/company-bank-settings').then((res) => res.data),
  updateCompanyBankSettings: (payload) =>
    api.put('/api/finance/company-bank-settings', payload).then((res) => res.data),
  getMonthlyPayouts: (year, month) =>
    api.get('/api/finance/payouts/monthly', { params: { year, month } }).then((res) => res.data),
  setPayoutLopOverride: (payload) =>
    api.post('/api/finance/payouts/lop-override', payload).then((res) => res.data),
  markPayoutsComplete: (payload) =>
    api.post('/api/finance/payouts/mark-complete', payload).then((res) => res.data),
  markPayoutsPending: (payload) =>
    api.post('/api/finance/payouts/mark-pending', payload).then((res) => res.data),
  getYearlyPayoutSummary: (year) =>
    api.get('/api/finance/payouts/yearly-summary', { params: { year } }).then((res) => res.data),
  downloadBankPayrollFile: async (year, month, resourceIds) => {
    try {
      const params = { year, month };
      if (Array.isArray(resourceIds) && resourceIds.length > 0) {
        params.resourceIds = resourceIds.join(',');
      }
      const response = await api.get('/api/finance/payouts/bank-file.xlsx', {
        params,
        responseType: 'blob'
      });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(contentDisposition);
      const filename =
        match?.[1] || `salary-payout-${year}-${String(month).padStart(2, '0')}.xlsx`;
      return { blob: response.data, filename };
    } catch (err) {
      let message = 'Failed to download bank file';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } catch (_) {
          /* ignore */
        }
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      const wrapped = new Error(message);
      wrapped.cause = err;
      throw wrapped;
    }
  }
};

// Partner Companies API
export const partnerCompaniesApi = {
  getByTechStack: (techStack) => api.get('/api/partner-companies/by-tech-stack', { 
    params: { tech_stack: techStack } 
  }).then(res => res.data)
};

// Hirelane API
export const hirelaneApi = {
  listVendors: () => api.get('/api/hirelane/vendors').then((res) => res.data),
  createVendor: (data) => api.post('/api/hirelane/vendors', data).then((res) => res.data),
  updateVendor: (vendorId, data) => api.put(`/api/hirelane/vendors/${vendorId}`, data).then((res) => res.data),
  deleteVendor: (vendorId) => api.delete(`/api/hirelane/vendors/${vendorId}`).then((res) => res.data),

  listInterviewers: () => api.get('/api/hirelane/interviewers').then((res) => res.data),
  createInterviewer: (data) => api.post('/api/hirelane/interviewers', data).then((res) => res.data),
  updateInterviewer: (interviewerId, data) =>
    api.put(`/api/hirelane/interviewers/${interviewerId}`, data).then((res) => res.data),
  deleteInterviewer: (interviewerId) =>
    api.delete(`/api/hirelane/interviewers/${interviewerId}`).then((res) => res.data),

  listJobs: (params = {}) => api.get('/api/hirelane/jobs', { params }).then((res) => res.data),
  createJob: (data) => api.post('/api/hirelane/jobs', data).then((res) => res.data),
  updateJob: (jobId, data) => api.put(`/api/hirelane/jobs/${jobId}`, data).then((res) => res.data),
  getJob: (jobId) => api.get(`/api/hirelane/jobs/${jobId}`).then((res) => res.data),
  generateJobDescription: (data) => api.post('/api/hirelane/jobs/ai-generate-jd', data).then((res) => res.data),
  parseJobIntent: (hiringIntent) =>
    api.post('/api/hirelane/jobs/ai-parse-intent', { hiring_intent: hiringIntent }).then((res) => res.data),

  listCandidates: (jobId, opts = {}) =>
    api
      .get(`/api/hirelane/jobs/${jobId}/candidates`, {
        params: opts.include ? { include: opts.include } : {}
      })
      .then((res) => res.data),
  listCandidateInterviews: (candidateId) =>
    api.get(`/api/hirelane/candidates/${candidateId}/interviews`).then((res) => res.data),
  scheduleInterview: (candidateId, data) =>
    api.post(`/api/hirelane/candidates/${candidateId}/interviews`, data).then((res) => res.data),
  patchInterview: (candidateId, interviewId, data) =>
    api
      .patch(`/api/hirelane/candidates/${candidateId}/interviews/${interviewId}`, data)
      .then((res) => res.data),
  sendCalendarInterviewInvites: (candidateId, interviewId) =>
    api
      .post(`/api/hirelane/candidates/${candidateId}/interviews/${interviewId}/send-calendar-invites`, {})
      .then((res) => res.data),
  recordInterviewOutcome: (candidateId, interviewId, data) =>
    api
      .post(`/api/hirelane/candidates/${candidateId}/interviews/${interviewId}/outcome`, data)
      .then((res) => res.data),
  reopenCandidate: (candidateId, data) =>
    api.post(`/api/hirelane/candidates/${candidateId}/reopen`, data).then((res) => res.data),
  /** Optional `resumeFile`: sends multipart with field `resume` so profile is stored and text extracted for Cony review. */
  createCandidate: (jobId, data, resumeFile) => {
    if (resumeFile) {
      const fd = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (value === '' && key !== 'name') return;
        fd.append(key, typeof value === 'string' || typeof value === 'number' ? String(value) : JSON.stringify(value));
      });
      fd.append('resume', resumeFile);
      return api
        .post(`/api/hirelane/jobs/${jobId}/candidates`, fd, {
          headers: { 'Content-Type': undefined }
        })
        .then((res) => res.data);
    }
    return api.post(`/api/hirelane/jobs/${jobId}/candidates`, data).then((res) => res.data);
  },
  updateCandidate: (candidateId, data) =>
    api.put(`/api/hirelane/candidates/${candidateId}`, data).then((res) => res.data),
  /** @param {object} payload `{ status, reason?, comments?, recommended_next_step? }` — reason & comments required for Rejected/Shortlisted; recommended_next_step required for Shortlisted */
  updateCandidateStatus: (candidateId, payload) =>
    api.post(`/api/hirelane/candidates/${candidateId}/status`, payload).then((res) => res.data),
  listCandidateStatusHistory: (candidateId) =>
    api.get(`/api/hirelane/candidates/${candidateId}/status-history`).then((res) => res.data),
  deleteCandidate: (candidateId) =>
    api.delete(`/api/hirelane/candidates/${candidateId}`).then((res) => res.data),
  getCandidateResumeUrl: (candidateId) =>
    api.get(`/api/hirelane/candidates/${candidateId}/resume-url`).then((res) => res.data),

  parseResumeAndExtractDraft: (jobId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post(`/api/hirelane/jobs/${jobId}/candidates/resume/parse`, formData, {
        headers: { 'Content-Type': undefined }
      })
      .then((res) => res.data);
  },
  createCandidateFromExtraction: (jobId, data) =>
    api.post(`/api/hirelane/jobs/${jobId}/candidates/from-extraction`, data).then((res) => res.data),

  evaluateCandidate: (candidateId) =>
    api.post(`/api/hirelane/candidates/${candidateId}/evaluate`, {}).then((res) => res.data),
  listEvaluations: (candidateId) =>
    api.get(`/api/hirelane/candidates/${candidateId}/evaluations`).then((res) => res.data)
};

/** No Supabase JWT — public Hirelane clarification endpoints use token in query/body only. */
const hirelaneClarificationPublicClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: config.api.timeout
});

export const hirelaneClarificationApi = {
  listSessions: (candidateId) =>
    api.get(`/api/hirelane/clarifications/candidates/${candidateId}/sessions`).then((res) => res.data),
  candidateStats: (candidateId) =>
    api.get(`/api/hirelane/clarifications/candidates/${candidateId}/stats`).then((res) => res.data),
  createDraft: (candidateId) =>
    api.post(`/api/hirelane/clarifications/candidates/${candidateId}/draft`).then((res) => res.data),
  deleteDraft: (sessionId) => api.delete(`/api/hirelane/clarifications/sessions/${sessionId}`).then((res) => res.data),
  patchDraft: (sessionId, body) =>
    api.patch(`/api/hirelane/clarifications/sessions/${sessionId}`, body).then((res) => res.data),
  sendSession: (sessionId) => api.post(`/api/hirelane/clarifications/sessions/${sessionId}/send`).then((res) => res.data),
  getSession: (sessionId) => api.get(`/api/hirelane/clarifications/sessions/${sessionId}`).then((res) => res.data),
  recruiterMessage: (sessionId, body, opts = {}) =>
    api
      .post(`/api/hirelane/clarifications/sessions/${sessionId}/messages`, {
        body,
        notify: opts.notify !== false
      })
      .then((res) => res.data),
  injectQuestion: (sessionId, question_text) =>
    api.post(`/api/hirelane/clarifications/sessions/${sessionId}/questions`, { question_text }).then((res) => res.data),
  listAvailableAutoQuestions: (sessionId) =>
    api.get(`/api/hirelane/clarifications/sessions/${sessionId}/available-auto-questions`).then((res) => res.data),
  addAutoQuestions: (sessionId, triggering_fields, question_overrides) =>
    api
      .post(`/api/hirelane/clarifications/sessions/${sessionId}/auto-questions`, {
        triggering_fields,
        ...(question_overrides && Object.keys(question_overrides).length
          ? { question_overrides }
          : {})
      })
      .then((res) => res.data),
  closeSession: (sessionId) =>
    api.post(`/api/hirelane/clarifications/sessions/${sessionId}/close`).then((res) => res.data),
  markRead: (sessionId) =>
    api.post(`/api/hirelane/clarifications/sessions/${sessionId}/mark-read`).then((res) => res.data),

  publicSession: (token, { advance = true } = {}) =>
    hirelaneClarificationPublicClient
      .get('/api/hirelane/clarifications/public/session', {
        params: { t: token, ...(advance ? {} : { advance: '0' }) }
      })
      .then((res) => res.data),
  publicPing: (token) =>
    hirelaneClarificationPublicClient
      .post('/api/hirelane/clarifications/public/ping', { token })
      .then((res) => res.data),
  publicSkip: (token) =>
    hirelaneClarificationPublicClient
      .post('/api/hirelane/clarifications/public/skip', { token })
      .then((res) => res.data),
  publicMessage: (token, text, files = []) => {
    const fd = new FormData();
    fd.append('token', token);
    if (text != null && String(text).trim()) fd.append('text', String(text));
    (files || []).forEach((f) => fd.append('files', f));
    return hirelaneClarificationPublicClient
      .post('/api/hirelane/clarifications/public/messages', fd)
      .then((res) => res.data);
  }
};

// Projects API
export const projectApi = {
  getAll: () => api.get('/api/projects').then(res => res.data),
  get: (projectId) => api.get(`/api/projects/${projectId}`).then(res => res.data),
  getMyProjects: () => api.get('/api/projects/my-projects').then(res => res.data),
  getAccessibleProjects: () => api.get('/api/projects/accessible').then(res => res.data),
  getTimesheet: (projectId, filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.userId) params.userId = filters.userId;
    return api.get(`/api/projects/${projectId}/timesheet`, { params }).then(res => res.data);
  },
  getProjectNotes: (projectId) => api.get(`/api/projects/${projectId}/notes`).then(res => res.data),
  updateNotes: (projectId, notes) => api.patch(`/api/projects/${projectId}/notes`, { notes }).then(res => res.data),
  getArtifacts: (projectId) => api.get(`/api/projects/${projectId}/artifacts`).then(res => res.data),
  uploadArtifacts: (projectId, files) => {
    const formData = new FormData();
    // Support both single file and array of files
    if (Array.isArray(files)) {
      files.forEach(file => formData.append('files', file));
    } else {
      formData.append('files', files);
    }
    return api.post(`/api/projects/${projectId}/artifacts`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(res => res.data);
  },
  deleteArtifact: (projectId, artifactId) => {
    return api.delete(`/api/projects/${projectId}/artifacts/${artifactId}`).then(res => res.data);
  },
  getTodayPeerTasks: (projectId) => api.get(`/api/projects/${projectId}/work-plans/today/peers`).then(res => res.data),
  getTodayWorkPlans: (projectId) => api.get(`/api/projects/${projectId}/work-plans/today`).then(res => res.data),
  getActivityHistory: (projectId, startDate, endDate) => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get(`/api/projects/${projectId}/activity-history`, { params }).then(res => res.data);
  }
};

// Manager API
export const managerApi = {
  getUsers: () => api.get('/api/manager/users').then(res => res.data),
  getTimeCapsuleData: (userId, startDate, endDate) => 
    api.get(`/api/manager/time-capsule/${userId}?startDate=${startDate}&endDate=${endDate}`).then(res => res.data),
  getBirdsviewData: (date) => api.get(`/api/manager/birdsview?date=${date}`).then(res => res.data),
  getWorkPlanSummary: (userId, date) => api.get(`/api/manager/summary/${userId}/${date}`).then(res => res.data),
  getWorkPlanHistory: (userId) => api.get(`/api/manager/history/${userId}`).then(res => res.data),
  getYesterdaySummary: () => api.get('/api/manager/yesterday-summary').then(res => res.data),
  getTimeOffs: (year, month) => api.get(`/api/manager/time-offs/${year}/${month}`).then(res => res.data),
  getTaskNotifications: (taskId) => api.get(`/api/manager/task-notifications/${taskId}`).then(res => res.data),
  getTaskNotificationsBatch: (taskIds) => api.post('/api/manager/task-notifications-batch', { taskIds }).then(res => res.data),
  // Conversation methods
  getTaskConversations: (taskId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    const queryString = params.toString();
    return api.get(`/api/manager/conversations/task/${taskId}${queryString ? `?${queryString}` : ''}`).then(res => res.data);
  },
  getUserConversations: (userId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.projectId) params.append('projectId', filters.projectId);
    const queryString = params.toString();
    return api.get(`/api/manager/conversations/user/${userId}${queryString ? `?${queryString}` : ''}`).then(res => res.data);
  },
  getAllConversations: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.taskId) params.append('taskId', filters.taskId);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.projectId) params.append('projectId', filters.projectId);
    const queryString = params.toString();
    return api.get(`/api/manager/conversations${queryString ? `?${queryString}` : ''}`).then(res => res.data);
  }
};

// Releases API
export const releasesApi = {
  // Get all releases for a project
  getProjectReleases: (projectId) => api.get(`/api/releases/project/${projectId}`).then(res => res.data),
  
  // Get single release with details
  getRelease: (releaseId) => api.get(`/api/releases/${releaseId}`).then(res => res.data),
  
  // Create a new release
  createRelease: (projectId, releaseData) => api.post(`/api/releases/project/${projectId}`, releaseData).then(res => res.data),
  
  // Update a release
  updateRelease: (releaseId, releaseData) => api.put(`/api/releases/${releaseId}`, releaseData).then(res => res.data),
  
  // Delete a release
  deleteRelease: (releaseId) => api.delete(`/api/releases/${releaseId}`).then(res => res.data),
  
  // Add feature tag to release
  addFeatureTag: (releaseId, featureTagId) => api.post(`/api/releases/${releaseId}/feature-tags`, { feature_tag_id: featureTagId }).then(res => res.data),
  
  // Remove feature tag from release
  removeFeatureTag: (releaseId, featureTagId) => api.delete(`/api/releases/${releaseId}/feature-tags/${featureTagId}`).then(res => res.data),
  
  // Get release health insights
  getInsights: (releaseId) => api.get(`/api/releases/${releaseId}/insights`).then(res => res.data)
};

// Social Wall API
export const socialWallApi = {
  // Get all posts (paginated)
  getAll: (page = 1, limit = 20, postType = 'all') => {
    const params = { page, limit };
    if (postType !== 'all') params.post_type = postType;
    return api.get('/api/social-wall', { params }).then(res => res.data);
  },
  
  // Get single post
  get: (postId) => api.get(`/api/social-wall/${postId}`).then(res => res.data),
  
  // Create new post (with optional files)
  create: (postData, files = []) => {
    const formData = new FormData();
    formData.append('content', postData.content);
    if (postData.post_type) formData.append('post_type', postData.post_type);
    
    // Add files if any
    if (files && files.length > 0) {
      files.forEach(file => {
        formData.append('files', file);
      });
    }
    
    return api.post('/api/social-wall', formData, {
      headers: { 'Content-Type': undefined }
    }).then(res => res.data);
  },
  
  // Update post
  update: (postId, postData) => api.put(`/api/social-wall/${postId}`, postData).then(res => res.data),
  
  // Delete post
  delete: (postId) => api.delete(`/api/social-wall/${postId}`).then(res => res.data),
  
  // Toggle like
  like: (postId) => api.post(`/api/social-wall/${postId}/like`).then(res => res.data),
  
  // Get comments for a post
  getComments: (postId) => api.get(`/api/social-wall/${postId}/comments`).then(res => res.data),
  
  // Add comment
  addComment: (postId, content) => api.post(`/api/social-wall/${postId}/comments`, { content }).then(res => res.data),
  
  // Update comment
  updateComment: (commentId, content) => api.put(`/api/social-wall/comments/${commentId}`, { content }).then(res => res.data),
  
  // Delete comment
  deleteComment: (commentId) => api.delete(`/api/social-wall/comments/${commentId}`).then(res => res.data),
  
  // Get notifications
  getNotifications: (page = 1, limit = 20, unreadOnly = false) => {
    const params = { page, limit };
    if (unreadOnly) params.unread_only = 'true';
    return api.get('/api/social-wall/notifications', { params }).then(res => res.data);
  },
  
  // Get unread notification count
  getUnreadCount: () => api.get('/api/social-wall/notifications/unread-count').then(res => res.data),
  
  // Mark notification as read
  markAsRead: (notificationId) => api.put(`/api/social-wall/notifications/${notificationId}/read`).then(res => res.data),
  
  // Mark all notifications as read
  markAllAsRead: () => api.put('/api/social-wall/notifications/mark-all-read').then(res => res.data)
};

export default api; 